import { ctaClickHandler } from '@mnd-apps/public/lib/tracker';
import type { TextMediaSectionFragment } from '@mnd-apps/public/wp-generated/types';
import Badge from '@mnd-frontend/ui/public/components/Badge';
import Button from '@mnd-frontend/ui/public/components/Button';
import CardBase, { CardBaseStyle } from '@mnd-frontend/ui/public/components/CardBase';
import Heading from '@mnd-frontend/ui/public/components/typography/Heading';
import Wysiwyg from '@mnd-frontend/ui/public/components/typography/Wysiwyg';
import ContentMaxWidth from '@mnd-frontend/ui/public/components/wrappers/ContentMaxWidth';
import { formatUrl } from '@mnd-frontend/ui/public/utils/formatters/url';
import { BREAKPOINTS, SPACING_2, SPACING_3, SPACING_10 } from '@mnd-frontend/ui/shared/tokens';
import { kebabCase } from 'lodash-es';
import Image from 'next/image';
import styled, { css } from 'styled-components';

const Container = styled(ContentMaxWidth)<{ $layoutReverse?: boolean }>`
  display: flex;
  gap: ${SPACING_3};
  padding: ${SPACING_3} ${SPACING_2};
  flex-direction: column-reverse;

  @media (min-width: ${BREAKPOINTS.MD}px) {
    gap: ${SPACING_10};
    padding-top: ${SPACING_10};
    padding-bottom: ${SPACING_10};
    flex-direction: ${({ $layoutReverse }) => ($layoutReverse ? 'row-reverse' : 'row')};
  }

  @media (min-width: ${BREAKPOINTS.XL}px) {
    padding-right: 0;
    padding-left: 0;
  }
`;

const TextWrapper = styled.div`
  flex: 1;
  display: grid;
  gap: ${SPACING_2};
`;

const Content = styled.div`
  display: grid;
  gap: ${SPACING_3};
  justify-items: left;
`;

// #region Media components
const MediaWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const MediaStyle = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 25rem;
`;

const MediaImage = styled(Image)`
  ${MediaStyle}
  ${CardBaseStyle}
`;

const StyledVideo = styled(CardBase)`
  ${MediaStyle}
`;

type MediaVideoProps = {
  src: string;
  height: number | undefined;
  width: number | undefined;
  role?: 'presentation';
};

const MediaVideo = ({ src, width, role }: MediaVideoProps) => (
  <StyledVideo as="video" autoPlay muted loop role={role} width={width}>
    <source src={src} type="video/mp4" />
  </StyledVideo>
);

type FormatMediaPropsReturnType = {
  alt: string;
  loading?: 'lazy' | 'eager' | undefined;
  sizes?: string;
} & MediaVideoProps;

const formatMediaProps = (
  data: TextMediaSectionFragment,
  isImage: boolean,
): FormatMediaPropsReturnType => ({
  alt: isImage ? data?.imageMedia?.node?.altText || '' : data?.videoMedia?.node.caption || '',
  src: data?.[isImage ? 'imageMedia' : 'videoMedia']?.node.mediaItemUrl ?? '',
  get role() {
    return !this.alt ? 'presentation' : undefined;
  },
  height: data?.[isImage ? 'imageMedia' : 'videoMedia']?.node.mediaDetails?.height ?? 165,
  width: data?.[isImage ? 'imageMedia' : 'videoMedia']?.node.mediaDetails?.width ?? 580,
  sizes: data?.imageMedia?.node.sizes || undefined,
  loading: isImage ? 'lazy' : undefined,
});
// #endregion

const TextMediaSectionModule = ({ moduleData }: { moduleData: TextMediaSectionFragment }) => {
  const sectionId = kebabCase(moduleData?.title ?? '');

  return (
    <Container as="article" $layoutReverse={moduleData?.layout?.[0] === 'image_left'}>
      <TextWrapper>
        {moduleData?.kicker && <Badge text={moduleData?.kicker} />}
        <Content>
          <Heading as="h2" id={sectionId}>
            {moduleData?.title}
          </Heading>
          <Wysiwyg htmlContent={moduleData?.description} />
          {moduleData?.link && (
            <Button
              href={formatUrl(moduleData?.link?.url ?? '')}
              newTab={moduleData?.link?.target === '_blank'}
              configuration="secondary"
              onClick={ctaClickHandler('text_media_section')}
              aria-labelledby={sectionId}
            >
              {moduleData?.link.title ?? ''}
            </Button>
          )}
        </Content>
      </TextWrapper>
      <MediaWrapper>
        {moduleData.imageMedia && <MediaImage {...formatMediaProps(moduleData, true)} />}
        {moduleData.videoMedia && <MediaVideo {...formatMediaProps(moduleData, false)} />}
      </MediaWrapper>
    </Container>
  );
};

export default TextMediaSectionModule;
